import React, { useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Alert, Box, CircularProgress, Tooltip, Typography } from '@mui/material';
import { GridColDef, GridRenderCellParams, GridRowParams } from '@mui/x-data-grid';
import { PageWrapper } from '../../../styledComponents/wrappers';
import { StripedDataGrid } from '../../../styledComponents/datagrid';
import { getLpuQueue, getLpuStagedOrders } from '../../../services/fulfillment/lpuApi';
import { ItemType, LpuOrder, OrderType } from '../../../services/fulfillment/types';
import { ORDER_TYPE_LABELS } from '../../../services/fulfillment/constants';
import { FormatDateString } from '../../../utils/helper';

const deriveOrderType = (itemTypes: ItemType[]): OrderType => {
  const hasBike = itemTypes.some(t => t === 'bike' || t === 'frame' || t === 'wheel');
  const hasPaa = itemTypes.some(t => t === 'paa');
  if (hasBike && hasPaa) return 'mixed';
  if (hasBike) return 'bike_only';
  if (hasPaa) return 'paa_only';
  return 'unknown';
};

export const MOCK_LPU_ROUTE = '/fulfillment/mock/lpu';
export const MOCK_LPU_STAGING_ROUTE = '/fulfillment/mock/lpu/:orderId/staging';
export const MOCK_LPU_PICKUP_ROUTE = '/fulfillment/mock/lpu/:orderId/pickup';

const ageDays = (isoDate: string) =>
  Math.floor((Date.now() - new Date(isoDate).getTime()) / (1000 * 60 * 60 * 24));

const AgeCell: React.FC<{ isoDate: string; warnDays: number }> = ({ isoDate, warnDays }) => {
  const days = ageDays(isoDate);
  const warn = days >= warnDays;
  return (
    <Tooltip title={warn ? `Over ${warnDays} days` : ''}>
      <Box
        sx={{
          px: 1,
          py: 0.25,
          borderRadius: 1,
          fontSize: 12,
          fontWeight: warn ? 700 : 400,
          bgcolor: warn ? '#fef2f2' : 'transparent',
          color: warn ? '#b91c1c' : 'text.secondary',
          border: warn ? '1px solid #fecaca' : 'none',
        }}
      >
        {days}d
      </Box>
    </Tooltip>
  );
};

const MockLpuQueue: React.FC = () => {
  const history = useHistory();
  const [queue, setQueue] = useState<LpuOrder[]>([]);
  const [staged, setStaged] = useState<LpuOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    Promise.all([getLpuQueue(), getLpuStagedOrders()])
      .then(([q, s]) => {
        setQueue(q);
        setStaged(s);
      })
      .catch(() => setError('Failed to load LPU queue.'))
      .finally(() => setLoading(false));
  }, []);

  const sortedQueue = useMemo(
    () => [...queue].sort((a, b) => a.createdAt.localeCompare(b.createdAt)),
    [queue],
  );

  const sortedStaged = useMemo(
    () =>
      [...staged].sort((a, b) =>
        (a.stagedAt ?? a.createdAt).localeCompare(b.stagedAt ?? b.createdAt),
      ),
    [staged],
  );

  const queueColumns: GridColDef[] = useMemo(
    () => [
      {
        headerName: 'Order #',
        field: 'shopifyOrderName',
        flex: 0.8,
      },
      {
        headerName: 'Customer',
        field: 'customer',
        flex: 1,
        valueGetter: (params: GridRenderCellParams<LpuOrder>) =>
          `${params.row.customer.firstName} ${params.row.customer.lastName}`,
      },
      {
        headerName: 'Type',
        field: 'orderType',
        flex: 0.7,
        sortable: false,
        valueGetter: (params: GridRenderCellParams<LpuOrder>) =>
          ORDER_TYPE_LABELS[deriveOrderType(params.row.lines.map(l => l.itemType))],
      },
      {
        headerName: 'Items',
        field: 'lineItemCount',
        flex: 0.4,
        type: 'number',
        valueGetter: (params: GridRenderCellParams<LpuOrder>) => params.row.lines.length,
      },
      {
        headerName: 'Order date',
        field: 'createdAt',
        flex: 0.9,
        valueFormatter: ({ value }) => (value ? FormatDateString(value) : ''),
      },
      {
        headerName: 'Age',
        field: 'age',
        flex: 0.5,
        sortable: false,
        renderCell: (params: GridRenderCellParams<LpuOrder>) => (
          <AgeCell isoDate={params.row.createdAt} warnDays={3} />
        ),
      },
    ],
    [],
  );

  const stagedColumns: GridColDef[] = useMemo(
    () => [
      {
        headerName: 'Order #',
        field: 'shopifyOrderName',
        flex: 0.8,
      },
      {
        headerName: 'Customer',
        field: 'customer',
        flex: 1,
        valueGetter: (params: GridRenderCellParams<LpuOrder>) =>
          `${params.row.customer.firstName} ${params.row.customer.lastName}`,
      },
      {
        headerName: 'Type',
        field: 'orderType',
        flex: 0.7,
        sortable: false,
        valueGetter: (params: GridRenderCellParams<LpuOrder>) =>
          ORDER_TYPE_LABELS[deriveOrderType(params.row.lines.map(l => l.itemType))],
      },
      {
        headerName: 'Items',
        field: 'lineItemCount',
        flex: 0.4,
        type: 'number',
        valueGetter: (params: GridRenderCellParams<LpuOrder>) => params.row.lines.length,
      },
      {
        headerName: 'Staged',
        field: 'stagedAt',
        flex: 0.9,
        valueFormatter: ({ value }) => (value ? FormatDateString(value) : '—'),
      },
      {
        headerName: 'Days waiting',
        field: 'daysWaiting',
        flex: 0.6,
        sortable: false,
        renderCell: (params: GridRenderCellParams<LpuOrder>) =>
          params.row.stagedAt ? (
            <AgeCell isoDate={params.row.stagedAt} warnDays={7} />
          ) : (
            <Typography sx={{ fontSize: 12, color: 'grey.400' }}>—</Typography>
          ),
      },
    ],
    [],
  );

  return (
    <PageWrapper>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
        <Typography variant="h5">Local Pickup Queue</Typography>
        <Box
          component="span"
          sx={{ fontSize: 12, fontWeight: 400, color: 'grey.500', verticalAlign: 'middle' }}
        >
          MOCK
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box display="flex" justifyContent="center" mt={4}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
            Ready to Stage
          </Typography>
          <StripedDataGrid
            rows={sortedQueue}
            columns={queueColumns}
            getRowId={row => row.shopifyOrderId}
            autoHeight
            onRowClick={(params: GridRowParams) =>
              history.push(
                `/fulfillment/mock/lpu/${(params.row as LpuOrder).shopifyOrderId}/staging`,
              )
            }
            sx={{ cursor: 'pointer', mb: 4 }}
            rowHeight={52}
            pageSizeOptions={[10, 25]}
            initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
          />

          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
            Staged / Awaiting Pickup
          </Typography>
          <StripedDataGrid
            rows={sortedStaged}
            columns={stagedColumns}
            getRowId={row => row.shopifyOrderId}
            autoHeight
            onRowClick={(params: GridRowParams) =>
              history.push(
                `/fulfillment/mock/lpu/${(params.row as LpuOrder).shopifyOrderId}/pickup`,
              )
            }
            sx={{ cursor: 'pointer' }}
            rowHeight={52}
            pageSizeOptions={[10, 25]}
            initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
          />
        </>
      )}
    </PageWrapper>
  );
};

export default MockLpuQueue;
